/**
 * FormActionBar — single top action bar for the form panel (title, company/status
 * context, Guardar/Cancelar or Regresar/Editar, and an "extra actions" kebab).
 *
 * Verified against MasterCrud's live "modo página" header
 * (MasterCrud.tsx:1568-1697 — CompanySelector + Activo/Edición badges left,
 * Cancelar + Guardar + kebab right). Matched pixel-for-pixel:
 * - Title: text-xl font-semibold tracking-tight text-[#1355b7] dark:text-primary-600
 * - Status badge: h-[18px] px-1.5 text-[10px] font-semibold rounded (not rounded-full)
 * - Editing/Creating badge: ONE style (amber, PencilIcon) for both states, text differs
 * - Cancelar/Guardar: size="base" + "!text-sm font-medium", w-4 h-4 icons
 * - Extra-actions trigger is an ICON-ONLY kebab (EllipsisVerticalIcon, w-9 h-9)
 *   placed AFTER Guardar — not a labeled "Acciones" button before it.
 *
 * Shows dirty indicator (•) in title when isDirty=true.
 *
 * @module components/form/FormActionBar
 * @see FMPV-MVWC-E003-S001 I-003, FMPV-MVWC-E004-S001 I-002
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  XMarkIcon,
  PencilIcon,
  EyeIcon,
  PlusIcon,
  MinusCircleIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon as RotateCcwIcon,
  ArrowLeftIcon,
  GlobeAltIcon,
  EllipsisVerticalIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Building2 } from 'lucide-react';
import { Button } from '../../../Button/Button';
import type { FormMode } from '../../types/internal.types';
import type { MasterPatternViewCompany } from '../../types/service.types';
import { CompanySelector } from '../toolbar/CompanySelector';

// Exact custom "save" glyph MasterCrud draws inline (Heroicons has no floppy-disk icon).
const SaveIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
    <path d="M7 3v4a1 1 0 0 0 1 1h7" />
  </svg>
);

export interface FormActionBarProps {
  mode: FormMode;
  title: string;
  isDirty: boolean;
  onSave: () => void;
  onCancel: () => void;
  onEdit: () => void;
  /** Called when user selects "Crear nuevo registro" from the extra-actions kebab (editing mode only) */
  onHamburgerCreate?: () => void;
  /** Called when user selects "Cambiar estado" from the extra-actions kebab (editing mode only) */
  onHamburgerChangeStatus?: () => void;
  /** Called when user selects "Habilitar compañía" (non-UNIVERSAL types only) */
  onHamburgerEnableCompany?: () => void;
  /** Called when user selects "Auditoría de Cambios" — omit to hide the item. */
  onHamburgerAudit?: () => void;
  /** Called when user selects "Usar datos globales" (company context only) */
  onHamburgerUseGlobalData?: () => void;
  /** Called when user toggles "Mostrar datos globales" (company context only) */
  onHamburgerToggleShowGlobal?: () => void;
  /** Current state of "Mostrar datos globales" toggle */
  showGlobalValues?: boolean;
  /** Company context — undefined hides the selector (UNIVERSAL) */
  companies?: MasterPatternViewCompany[];
  activeCompanyId?: string;
  onCompanyChange?: (company: MasterPatternViewCompany) => void;
  /** Company ids this GLOBAL record is enabled for — shown as a per-company indicator in the dropdown. Omit to hide (e.g. record still loading, or non-GLOBAL type). */
  assignedCompanyIds?: string[];
  definitionType?: string;
  /** Current record's active status — shown as badge next to company selector */
  recordIsActive?: boolean;
  /** Compact mode: renders action buttons icon-only (no labels) to save horizontal space (sidebar) */
  compact?: boolean;
  /** Disables the Guardar button — used when the full record failed to load from the server. */
  saveDisabled?: boolean;
  /** Disables the company selector — prevents company switching in certain contexts. */
  companyDisabled?: boolean;
  /** Hides the Editar button completely in view mode. */
  editHidden?: boolean;
  /** Renders the Editar button as disabled (grayed out, not clickable) in view mode. */
  editGrayed?: boolean;
  /** Tooltip text shown on the grayed-out Editar button to explain why editing is unavailable. */
  editGrayedReason?: string;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

const BUTTON_TEXT_CLASS = '!text-sm font-medium';

export function FormActionBar({
  mode,
  title,
  isDirty,
  onSave,
  onCancel,
  onEdit,
  onHamburgerCreate,
  onHamburgerChangeStatus,
  onHamburgerEnableCompany,
  onHamburgerAudit,
  onHamburgerUseGlobalData,
  onHamburgerToggleShowGlobal,
  showGlobalValues = false,
  companies,
  activeCompanyId,
  onCompanyChange,
  assignedCompanyIds,
  definitionType,
  recordIsActive,
  compact = false,
  saveDisabled = false,
  companyDisabled = false,
  editHidden = false,
  editGrayed = false,
  editGrayedReason,
  t,
}: FormActionBarProps) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const displayTitle = isDirty ? `• ${title}` : title;
  const isViewing = mode === 'viewing';
  const isEditing = mode === 'editing';
  const isCreating = mode === 'creating';
  const showCompany = definitionType !== 'UNIVERSAL';

  // "company context" = a specific company is selected (not global / not UNIVERSAL)
  const isCompanyContext = showCompany && !!activeCompanyId && activeCompanyId !== 'global';

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);

  const close = useCallback((fn?: () => void) => {
    setMenuOpen(false);
    fn?.();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 p-5 pb-2">
      <div className="flex flex-col gap-2 min-w-0">
        <span data-testid="form-title" className="text-xl font-semibold tracking-tight text-[#1355b7] dark:text-primary-600 truncate">
          {displayTitle}
        </span>
        {/* Meta row: company context + status badge + creating/editing/readonly badge */}
        <div className="flex items-center gap-2 flex-wrap">
          {showCompany && (
            <CompanySelector
              companies={companies ?? []}
              activeCompanyId={activeCompanyId}
              onCompanyChange={onCompanyChange ?? (() => {})}
              assignedCompanyIds={assignedCompanyIds}
              showGlobalOption={definitionType === 'GLOBAL'}
              disabled={companyDisabled}
            />
          )}
          {recordIsActive !== undefined && (
            <span
              data-testid="form-status-badge"
              className={`inline-flex items-center h-[18px] px-1.5 text-[10px] font-medium leading-none rounded ${
                recordIsActive
                  ? 'bg-[#ecfccb] text-[#4d7c0f] dark:bg-lime-900/30 dark:text-lime-400'
                  : 'bg-background-secondary text-content-tertiary dark:bg-dark-bg-secondary dark:text-dark-content-tertiary'
              }`}
            >
              {recordIsActive ? tt('active', 'Activo') : tt('inactive', 'Inactivo')}
            </span>
          )}
          {(isCreating || isEditing) && (
            <span
              data-testid="form-mode-badge"
              className="inline-flex items-center gap-1 h-[18px] px-1.5 text-[10px] font-medium leading-none rounded bg-[#fef9c3] text-[#a16207] dark:bg-yellow-900/20 dark:text-yellow-500 w-fit"
            >
              <PencilIcon className="w-3 h-3" />
              {isCreating ? tt('creating', 'Creación') : tt('editing', 'Edición')}
            </span>
          )}
          {isViewing && (
            <span
              data-testid="form-readonly-badge"
              className="inline-flex items-center gap-1 h-[18px] px-1.5 text-[10px] font-medium leading-none rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 w-fit"
            >
              <EyeIcon className="w-3 h-3" />
              {tt('readOnly', 'Solo lectura')}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {isViewing ? (
          <>
            <Button type="outline" size="base" className={BUTTON_TEXT_CLASS} leftIcon={<ArrowLeftIcon className="w-4 h-4" />} data-testid="form-back-button" title={tt('back', 'Regresar')} onClick={onCancel}>
              {!compact && tt('back', 'Regresar')}
            </Button>
            {!editHidden && (
              <Button
                type="default"
                size="base"
                className={BUTTON_TEXT_CLASS}
                leftIcon={<PencilIcon className="w-4 h-4" />}
                data-testid="form-edit-button"
                title={editGrayed ? (editGrayedReason ?? tt('editUnavailable', 'Editar no disponible')) : tt('edit', 'Editar')}
                disabled={editGrayed}
                onClick={onEdit}
              >
                {!compact && tt('edit', 'Editar')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button type="outline" size="base" className={BUTTON_TEXT_CLASS} leftIcon={<XMarkIcon className="w-4 h-4" />} data-testid="form-cancel-button" title={tt('discardChanges', 'Cancelar')} onClick={onCancel}>
              {!compact && tt('discardChanges', 'Cancelar')}
            </Button>
            <Button type="default" size="base" className={BUTTON_TEXT_CLASS} leftIcon={<SaveIcon className="w-4 h-4" />} data-testid="form-save-button" title={tt('saveChanges', 'Guardar')} onClick={onSave} disabled={saveDisabled}>
              {!compact && tt('saveChanges', 'Guardar')}
            </Button>
          </>
        )}

        {/* Extra-actions kebab — icon-only, positioned AFTER Guardar (mirrors MasterCrud.tsx:1630-1641) */}
        {isEditing && (
          <div className="relative" ref={menuRef}>
            <motion.button
              type="button"
              data-testid="form-hamburger-button"
              onClick={toggleMenu}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              title={tt('moreActions', 'Más acciones')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.92 }}
              transition={{ type: 'spring', bounce: 0.55, duration: 0.4 }}
              className="inline-flex items-center justify-center w-9 h-9 border border-border-primary dark:border-dark-border-primary rounded-md bg-background-secondary dark:bg-dark-bg-secondary hover:bg-[#ebebec] dark:hover:bg-dark-bg-primary text-content-tertiary transition-colors"
            >
              <EllipsisVerticalIcon className="w-4 h-4" />
            </motion.button>

            {menuOpen && (() => {
              // Build the item list in display order, then render a SINGLE separator
              // at the midpoint (mirrors RowActions.tsx's kebab) instead of one per
              // section. "Cambiar estado" always goes last, red/Deshabilitar while
              // active or green/Habilitar while inactive — same treatment as the
              // per-row kebab's changeStatus entry.
              type MenuItem = {
                key: string;
                testId: string;
                icon: React.ReactNode;
                label: React.ReactNode;
                onClick: () => void;
                tone?: 'danger' | 'success';
                // Semantic group — a separator is rendered whenever this changes from the
                // previous item, instead of at a data-dependent numeric midpoint.
                group: 'global' | 'actions' | 'meta';
              };
              const items: MenuItem[] = [];

              if (definitionType === 'GLOBAL' && isCompanyContext) {
                items.push({
                  key: 'use-global',
                  testId: 'form-hamburger-use-global',
                  icon: <RotateCcwIcon className="w-4 h-4 text-[#1355b7] dark:text-primary-400 flex-shrink-0" />,
                  label: tt('useGlobalData', 'Usar datos globales'),
                  onClick: () => close(onHamburgerUseGlobalData),
                  group: 'global',
                });
                items.push({
                  key: 'show-global',
                  testId: 'form-hamburger-show-global',
                  icon: <GlobeAltIcon className="w-4 h-4 text-[#1355b7] dark:text-primary-400 flex-shrink-0" />,
                  label: (
                    <span className="flex items-center justify-between gap-2.5 flex-1">
                      <span className="whitespace-nowrap">{tt('showGlobalValuesLabel', 'Mostrar datos globales')}</span>
                      {/* Toggle — pixel-exact match to mock .dd-toggle-track / .dd-toggle-thumb */}
                      <span style={{
                        width: 28, height: 16, borderRadius: 8, flexShrink: 0,
                        background: showGlobalValues
                          ? 'var(--text-color-primary-custom-600)'
                          : 'var(--color-border-primary)',
                        position: 'relative', display: 'inline-block',
                        transition: 'background .2s',
                      }}>
                        <span style={{
                          width: 12, height: 12, borderRadius: '50%',
                          background: 'var(--color-bg-primary)',
                          position: 'absolute', top: 2,
                          left: showGlobalValues ? 14 : 2,
                          transition: 'left .15s',
                          boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                        }} />
                      </span>
                    </span>
                  ),
                  onClick: () => close(onHamburgerToggleShowGlobal),
                  group: 'global',
                });
              }

              if (definitionType === 'GLOBAL') {
                items.push({
                  key: 'enable-company',
                  testId: 'form-hamburger-enable-company',
                  icon: <Building2 className="w-4 h-4 text-content-tertiary flex-shrink-0" />,
                  label: tt('enableCompanyAction', 'Habilitar compañía'),
                  onClick: () => close(onHamburgerEnableCompany),
                  group: 'actions',
                });
              }

              items.push({
                key: 'create',
                testId: 'form-hamburger-create',
                icon: <PlusIcon className="w-4 h-4 text-content-tertiary flex-shrink-0" />,
                label: tt('createNewRecord', 'Crear nuevo registro'),
                onClick: () => close(onHamburgerCreate),
                group: 'actions',
              });

              if (onHamburgerAudit) {
                items.push({
                  key: 'audit',
                  testId: 'form-hamburger-audit',
                  icon: <ClockIcon className="w-4 h-4 text-content-tertiary flex-shrink-0" />,
                  label: tt('auditChanges', 'Auditoría de Cambios'),
                  onClick: () => close(onHamburgerAudit),
                  group: 'meta',
                });
              }

              // Always last.
              items.push({
                key: 'change-status',
                testId: 'form-hamburger-change-status',
                icon: recordIsActive
                  ? <MinusCircleIcon className="w-4 h-4 flex-shrink-0" />
                  : <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />,
                label: recordIsActive
                  ? tt('disableRecordAction', 'Desactivar registro')
                  : tt('enableRecordAction', 'Activar registro'),
                onClick: () => close(onHamburgerChangeStatus),
                tone: recordIsActive ? 'danger' : 'success',
                group: 'meta',
              });

              return (
                <div
                  data-testid="form-hamburger-menu"
                  role="menu"
                  className="absolute right-0 top-full mt-1 z-50 min-w-[260px] bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-md shadow-md py-1"
                >
                  {items.map((item, idx) => (
                    <React.Fragment key={item.key}>
                      {idx > 0 && item.group !== items[idx - 1].group && (
                        <hr className="my-1 border-border-primary dark:border-dark-border-primary" />
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        data-testid={item.testId}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                          item.tone === 'danger'
                            ? 'text-error-content hover:bg-error/10'
                            : item.tone === 'success'
                              ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30'
                              : 'text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'
                        }`}
                        onClick={item.onClick}
                      >
                        {item.icon}
                        {item.label}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
